/**
 * Service pour gérer les comptes Stripe Connect
 * 
 * ⚠️ SÉCURITÉ: Ce service appelle UNIQUEMENT le backend
 * Il ne contient JAMAIS de clés secrètes Stripe
 */

import { FUNCTIONS_BASE_URL } from '@/shared/config/environment';

export interface StripeAccountInfo {
  id: string;
  email?: string;
  country?: string;
  business_type?: string;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
  business_profile?: {
    name?: string;
    url?: string;
  };
  company?: {
    name?: string;
    address?: {
      line1?: string;
      city?: string;
      postal_code?: string;
      country?: string;
    };
  };
  requirements?: {
    currently_due?: string[];
    eventually_due?: string[];
    past_due?: string[];
    disabled_reason?: string;
    pending_verification?: string[];
  };
  capabilities?: any;
  created?: number;
}

export interface CreateAccountRequest {
  organizationId: string;
  country?: string;
  email?: string;
  businessType?: string;
  businessProfile?: {
    name?: string;
    url?: string;
  };
  company?: {
    address?: {
      line1?: string;
      city?: string;
      postal_code?: string;
      country?: string;
    };
  };
}

export interface CreateAccountWithLinkRequest extends CreateAccountRequest {
  refreshUrl: string;
  returnUrl: string;
}

export interface CreateAccountWithLinkResponse {
  accountId: string;
  onboardingUrl: string;
  expiresAt: number;
}

export interface CreateAccountLinkRequest {
  accountId: string;
  refreshUrl: string;
  returnUrl: string;
  collectionFields?: 'eventually_due' | 'currently_due';
}

export interface CreateAccountLinkResponse {
  url: string;
  expiresAt: number;
}

/**
 * Service pour interagir avec le backend Stripe Account
 */
export class StripeAccountService {
  /**
   * Récupère les informations d'un compte Stripe Connect
   * GET /stripe/account/:accountId
   */
  static async getAccountInfo(accountId: string): Promise<StripeAccountInfo> {
    try {
      const response = await fetch(
        `${FUNCTIONS_BASE_URL}/getStripeAccountInfo?accountId=${accountId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la récupération des informations du compte');
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Erreur getAccountInfo:', error);
      throw error;
    }
  }

  /**
   * Crée un compte Stripe Express et génère le lien d'onboarding en une seule requête
   * POST /stripe/account/create-with-link
   */
  static async createAccountWithLink(
    data: CreateAccountWithLinkRequest
  ): Promise<CreateAccountWithLinkResponse> {
    try {
      const response = await fetch(
        `${FUNCTIONS_BASE_URL}/createStripeAccountWithLink`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        // Créer une erreur avec les détails complets pour affichage
        const errorMessage = JSON.stringify(errorData);
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Erreur createAccountWithLink:', error);
      throw error;
    }
  }

  /**
   * Crée un compte Stripe Express uniquement
   * POST /stripe/account/create
   */
  static async createAccount(data: CreateAccountRequest): Promise<StripeAccountInfo> {
    try {
      const response = await fetch(
        `${FUNCTIONS_BASE_URL}/createStripeAccount`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la création du compte');
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Erreur createAccount:', error);
      throw error;
    }
  }

  /**
   * Crée un lien d'onboarding pour un compte existant
   * POST /stripe/account/create-link
   */
  static async createAccountLink(
    data: CreateAccountLinkRequest
  ): Promise<CreateAccountLinkResponse> {
    try {
      const response = await fetch(
        `${FUNCTIONS_BASE_URL}/createStripeAccountLink`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        // Créer une erreur avec les détails complets pour affichage
        const errorMessage = JSON.stringify(errorData);
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Erreur createAccountLink:', error);
      throw error;
    }
  }

  /**
   * Vérifie le statut d'un compte Stripe
   * GET /stripe/account/:accountId/status
   */
  static async checkAccountStatus(accountId: string): Promise<{
    accountId: string;
    status: 'active' | 'pending' | 'incomplete';
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    detailsSubmitted: boolean;
    requirements: {
      currentlyDue: string[];
      eventuallyDue: string[];
      pastDue: string[];
      disabledReason?: string;
    };
  }> {
    try {
      const response = await fetch(
        `${FUNCTIONS_BASE_URL}/checkStripeAccountStatus?accountId=${accountId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la vérification du statut');
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Erreur checkAccountStatus:', error);
      throw error;
    }
  }
}

// Export des fonctions individuelles pour faciliter l'utilisation
export const {
  getAccountInfo,
  createAccountWithLink,
  createAccount,
  createAccountLink,
  checkAccountStatus,
} = StripeAccountService;

